import { useNavigate, useParams } from "react-router-dom";
import {
  deliveryAndTransferValidationSchema,
  validationSchema,
  newAddressViewValidationSchema,
} from "./validationSchema";
import { MultiStepProgress } from "components/MultiStepProgress";
import { TextFieldComponent } from "components/basic/TextField";
import { SelectComponent } from "components/basic/Select";
import { Address } from "components/Address/Address";
import { useFormik } from "formik";
import { ButtonRound } from "components/basic/Buttons";
import { CustomisePlate } from "@/components/CustomisePlate/CustomisePlate";
import { Collapsible } from "@/components/basic/Collapsible";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import {
  getDeliveryPrices,
  getRegistrationDetails,
  getSalesFormMessage,
  initializeFormId,
  recoverFormValues,
  selectDeliveryPrices,
  selectFormValues,
  selectRegistrationDetails,
  selectSalesFormMessage,
  selectVehicleOptions,
  storeCustomiseValues,
} from "@/pages/CheckoutInfo/checkoutInfoSlice";
import { CheckoutCardRadios } from "@/components/CheckoutCardRadios/CheckoutCardRadios";
import {
  extraRadios,
  transferRadiosFactory,
  deliveryRadiosFactory,
} from "./constants";
import { PurchaserDetails } from "@/components/CustomiseForm/PurchaserDetails/PurchaserDetails";
import { selectUserInfo } from "@/core/store/authSlice";
import { progressInfosFactory } from "../constants";
import v5cnamearea from "@/assets/img/checkout/v5cnamearea.jpg";
import { Icon } from "@rmwc/icon";
import Switch from "@/components/basic/Switch/Switch";

const CURRENT_PAGE_NUMBER = 1;

const CheckoutCustomise = () => {
  const cardClassName = "md:p-12 p-8 bg-card-bg-grey rounded";
  const hadTryToSubmit = useRef(false);

  const [isFinance, setFinance] = useState(false);
  const newAddressView = true;

  const { registration: urlRegistration } = useParams();
  const registration = useMemo(() => {
    return urlRegistration?.replace(/\+/g, " ");
  }, [urlRegistration]);

  const progressInfos = useMemo(
    () => progressInfosFactory({ registration: registration || "" }),
    [registration]
  );
  const progressTitles = useMemo(
    () => progressInfos.map((info) => info.title),
    [progressInfos]
  );

  const navigate = useNavigate();

  const nextPage = () => {
    let url = progressInfos[CURRENT_PAGE_NUMBER + 1].path;
    if (isFinance) {
      url = url + "?finance=true";
    }
    navigate(url);
  };

  const prevPage = () => {
    navigate(progressInfos[CURRENT_PAGE_NUMBER - 1].path);
  };

  const dispatch = useAppDispatch();
  const vehicleYearOptionsString = useAppSelector(selectVehicleOptions);
  const registrationDetails = useAppSelector(selectRegistrationDetails);
  const deliveryPrices = useAppSelector(selectDeliveryPrices);
  const storeFormValues = useAppSelector(selectFormValues);
  const salesFormMessage = useAppSelector(selectSalesFormMessage);

  // We need to move these effects before formik initialization but not the cache effect
  useEffect(() => {
    dispatch(initializeFormId());
    const urlParams = new URLSearchParams(window.location.search);
    setFinance(urlParams.get("finance") === "true");
    document.title =
      "Buy " + registration + " Private Number Plate from New Reg";
  }, [dispatch, registration]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (storeFormValues?.formId && !storeFormValues?.year) {
      dispatch(
        recoverFormValues({
          id: storeFormValues?.formId,
        })
      );
    }
  }, [storeFormValues?.formId, storeFormValues?.year, dispatch]);

  const sourcecode = registrationDetails?.sourcecode;
  const existDeliveryAndTransfer = useMemo(() => {
    return sourcecode == "1";
  }, [sourcecode]);

  const formValidationSchema = useMemo(() => {
    if (existDeliveryAndTransfer) {
      if (newAddressView) {
        return newAddressViewValidationSchema.concat(deliveryAndTransferValidationSchema);
      } else {
        return validationSchema.concat(deliveryAndTransferValidationSchema);
      }
    } else {
      return newAddressView ? newAddressViewValidationSchema : validationSchema;
    }
  }, [existDeliveryAndTransfer, newAddressView]);

  const initialValues = useMemo(() => {
    return Object.assign(
      {
        email: storeFormValues?.email || "",
        title: storeFormValues?.title || "",
        surname: storeFormValues?.surname || "",
        forename: storeFormValues?.forename || "",
        telephone1: storeFormValues?.telephone1 || "",
        address1: storeFormValues?.address1 || "",
        address2: storeFormValues?.address2 || "",
        address3: storeFormValues?.address3 || "",
        address4: storeFormValues?.address4 || "",
        postcode: storeFormValues?.postcode || "",
        order_plate: storeFormValues?.order_plate || "no",
        year: storeFormValues?.year || "",
        vehicle_keeper: storeFormValues?.vehicle_keeper || "",
        gift_wrapping: storeFormValues?.gift_wrapping || "no",
        transfer_type: storeFormValues?.transfer_type || "",
        delivery_type: storeFormValues?.delivery_type || "",
        isManualAddress: !!storeFormValues?.address1,
        
        // Additional address fields for finance applications
        ...(newAddressView ? {
          flatNumber: storeFormValues?.flatNumber || "",
          buildingName: storeFormValues?.buildingName || "",
          buildingNumber: storeFormValues?.buildingNumber || "",
          street: storeFormValues?.street || "",
          locality: storeFormValues?.locality || "",
          townOrCity: storeFormValues?.townOrCity || "",
          county: storeFormValues?.county || ""
        } : {})
      },
      storeFormValues?.order_plate === "yes"
        ? {
            digit_style: storeFormValues?.digit_style,
            acrylic_plate_font: storeFormValues?.acrylic_plate_font,
            euro: storeFormValues?.euro,
            acrylic_plate_background: storeFormValues?.acrylic_plate_background,
            border: storeFormValues?.border,
            acrylic_plate_border: storeFormValues?.acrylic_plate_border,
            plate_size: storeFormValues?.plate_size,
            acrylic_plate_size: storeFormValues?.acrylic_plate_size,
            acrylic_plate_euro: storeFormValues?.acrylic_plate_euro,
          }
        : {}
    );
  }, [storeFormValues, newAddressView]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: formValidationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);
      const formId = storeFormValues?.formId;
      const data: { [key: string]: any } = {
        ...values,
        sourcecode,
        display_date1: registrationDetails?.display_date1,
        corr_or_garage: "Correspond With Me",
        registration,
        formId,
      };
      delete data.isManualAddress;
      if (!existDeliveryAndTransfer) {
        delete data.transfer_type;
        delete data.delivery_type;
      }
      if (data.order_plate === "no") {
        data.gift_wrapping = "no";
      }
      if (data.gift_wrapping === "yes") {
        data.gift_wrapping_toggle = "yes";
      }
      dispatch(
        storeCustomiseValues({
          data,
          continueCallback() {
            setSubmitting(false);
            nextPage();
          },
        })
      );
    },
  });
  const {
    values,
    errors,
    touched,
    handleChange,
    setFieldValue,
    handleBlur,
    isSubmitting,
  } = formik;

  // Get form data from sessionStorage when page loads (moved here to have access to setFieldValue)
  useEffect(() => {
    const cacheKey = registration ? `form_${registration}` : '';
    if (cacheKey) {
      try {
        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) {
          console.log('Found cached form data');
          const parsedData = JSON.parse(cachedData);
          
          // Restore form data to initialValues
          if (parsedData && Object.keys(parsedData).length > 0) {
            console.log('Restoring form data from cache');
            Object.keys(parsedData).forEach(key => {
              if (parsedData[key] !== undefined && parsedData[key] !== null) {
                setFieldValue(key, parsedData[key]);
              }
            });
            
            // If address info exists, set isManualAddress to true
            if (parsedData.address1 || (newAddressView && parsedData.street)) {
              setFieldValue('isManualAddress', true);
            }
          }
        }
      } catch (e) {
        console.error('Error reading cache:', e);
      }
    }
  }, [registration, newAddressView, setFieldValue]);

  // Save form values to sessionStorage when values change
  useEffect(() => {
    if (!registration) return;
    
    const cacheKey = `form_${registration}`;
    const saveFormData = () => {
      try {
        // Filter out undefined and null values
        const dataToSave: Record<string, any> = {};
        Object.keys(values).forEach(key => {
          if (values[key as keyof typeof values] !== undefined && 
              values[key as keyof typeof values] !== null) {
            dataToSave[key] = values[key as keyof typeof values];
          }
        });
        
        // Add timestamp for debugging
        dataToSave['_timestamp'] = new Date().toISOString();
        
        sessionStorage.setItem(cacheKey, JSON.stringify(dataToSave));
        console.log('Form data saved to cache');
      } catch (e) {
        console.error('Error saving to cache:', e);
      }
    };
    
    // Use debounce, only save after user stops typing for a while
    const timerId = setTimeout(saveFormData, 500);
    
    return () => clearTimeout(timerId);
  }, [values, registration]);

  const isCustomPlate = useMemo(
    () => values.order_plate === "yes",
    [values.order_plate]
  );

  const transferRadios = transferRadiosFactory({ registration });
  const deliveryRadios = !!deliveryPrices
    ? deliveryRadiosFactory(deliveryPrices)
    : [];

  useEffect(() => {
    dispatch(getDeliveryPrices());
  }, [dispatch]);

  useEffect(() => {
    if (registration) {
      dispatch(getSalesFormMessage({ registration }));
    }
  }, [registration, dispatch]);

  useEffect(() => {
    if (!vehicleYearOptionsString && registration) {
      dispatch(getRegistrationDetails({ registration }));
    }
  }, [vehicleYearOptionsString, registration, dispatch]);

  const vehicleYearOptions = useMemo(() => {
    if (vehicleYearOptionsString) {
      return vehicleYearOptionsString
        .split(",")
        .filter((value) => value !== "Please Select ")
        .filter((value) => !!value.replace(/(^\s*)/, ""))
        .map((value) => value.replace(/\./g, "/"))
        .map((value) => {
          const matchDate = value.match(
            /\d{1,2}\/\d{1,2}\/\d{1,2}\s*-\s*\d{1,2}\/\d{1,2}\/\d{1,2}/
          );
          if (matchDate) {
            const date = matchDate[0];
            return value.replace(date, `(${date})`);
          } else {
            return value;
          }
        });
    }
    return [];
  }, [vehicleYearOptionsString]);

  const userInfo = useAppSelector(selectUserInfo);
  const shouldFillFormUseUserInfo = useRef(true);
  useEffect(() => {
    if (shouldFillFormUseUserInfo.current) {
      if (values.postcode) {
        shouldFillFormUseUserInfo.current = false;
      }
    }
  }, [values.postcode]);

  useEffect(() => {
    const beforeUnloadEvent = (event: { returnValue: string }) =>
      (event.returnValue = "Leave Site?");
    window.addEventListener("beforeunload", beforeUnloadEvent);
    return () => {
      window.removeEventListener("beforeunload", beforeUnloadEvent);
    };
  }, []);

  useEffect(() => {
    if (userInfo && shouldFillFormUseUserInfo.current) {
      [
        "email",
        "title",
        "surname",
        "forename",
        "telephone1",
        "address1",
        "address2",
        "address3",
        "address4",
        // "flatNumber",
        // "buildingName",
        // "buildingNumber",
        // "street",
        // "locality",
        // "townOrCity",
        // "county",
        "postcode",
      ].forEach((key) => {
        let infoKey = key;
        switch (key) {
          case "email":
            infoKey = "user";
            break;
          default:
            break;
        }
        let infoValue = userInfo[infoKey] || "";
        switch (key) {
          case "postcode":
            infoValue = infoValue.toUpperCase();
            break;
          default:
            break;
        }
        setFieldValue(key, infoValue);
      });
      setFieldValue("isManualAddress", true);
      
      // If we have address1 data, try to parse it
      if (newAddressView && userInfo.address1) {
        const addressParts = userInfo.address1.split(" ");
        const buildingNumber = addressParts.length > 0 && !isNaN(Number(addressParts[0])) 
          ? addressParts[0] 
          : "";
        const street = addressParts.length > 0 
          ? addressParts.slice(buildingNumber ? 1 : 0).join(" ") 
          : userInfo.address1 || "";
          
        setFieldValue("buildingNumber", buildingNumber);
        setFieldValue("street", street);
        setFieldValue("locality", userInfo.address2 || "");
        setFieldValue("townOrCity", userInfo.address3 || "");
        setFieldValue("county", userInfo.address4 || "");
      }
    }
  }, [userInfo, newAddressView, setFieldValue]);

  return (
    <div>
      <MultiStepProgress
        stepNames={progressTitles}
        page={CURRENT_PAGE_NUMBER}
      />
      <form
        onSubmit={formik.handleSubmit}
        className="max-w-screen-sm w-11/12 flex-column mx-auto py-16"
      >
        <div>
          <h2 className="font-bold text-xl mb-6 text-left">
            Purchaser's Details
          </h2>
          <div className={`${cardClassName} pb-8 grid gap-4`}>
            <PurchaserDetails
              values={values}
              errors={errors}
              touched={touched}
              handleChange={handleChange}
              setFieldValue={setFieldValue}
              handleBlur={handleBlur}
            ></PurchaserDetails>
          </div>
          <div>
            <h2 className="font-bold text-xl my-6 text-left">
              Address Details
            </h2>
            <div className={`${cardClassName} grid gap-4`}>
              <Address
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
                handleBlur={handleBlur}
                selectErrorMsg={
                  touched?.isManualAddress ? errors?.isManualAddress : ""
                }
                newAddressView={newAddressView}
              ></Address>
            </div>
          </div>
          <div>
            <h2 className="font-bold text-xl my-6 text-left">
              Order number plates for your vehicle here:
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <button
                className={`md:col-span-1 md:h-20 h-10 border-solid rounded ${
                  !isCustomPlate
                    ? "bg-success border-success text-white"
                    : "bg-card-bg-grey text-black border-[#d3d3d3] border"
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  setFieldValue("order_plate", "no");
                }}
              >
                {!isCustomPlate && <span className="mr-1">✓</span>} No
              </button>
              <button
                className={`md:col-span-1 md:h-20 h-10 border-solid rounded ${
                  isCustomPlate
                    ? "bg-success border-success text-white"
                    : "bg-card-bg-grey text-black border-[#d3d3d3] border"
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  setFieldValue("order_plate", "yes");
                }}
              >
                {isCustomPlate && <span className="mr-1">✓</span>}
                Yes
              </button>
            </div>
            <div
              className={`${cardClassName} mt-8 ${
                isCustomPlate ? "" : "hidden"
              }`}
            >
              <CustomisePlate
                isCustomPlate={isCustomPlate}
                values={values}
                setFieldValue={setFieldValue}
                registration={registration || ""}
              ></CustomisePlate>
            </div>
          </div>
        </div>
        {isCustomPlate && (
          <div>
            <h2 className="font-bold text-xl my-6 text-left">Extras</h2>
            <div
              className="flex px-4 py-6 bg-card-bg-grey rounded cursor-pointer"
              onClick={() => {
                setFieldValue(
                  "gift_wrapping",
                  values.gift_wrapping !== "yes" ? "yes" : "no"
                );
              }}
            >
              <div className="mx-16 flex justify-center align-center text-3xl">
                <Icon icon={extraRadios[0].icon} className="text-3xl" />
              </div>
              <div className="text-left">
                <h3 className="font-bold text-lg">{extraRadios[0].title}</h3>
                <h3 className="text-lg">{extraRadios[0].price}</h3>
                <p
                  className="text-xs font-light"
                  dangerouslySetInnerHTML={{
                    __html: extraRadios[0].description,
                  }}
                ></p>
              </div>
              <div className="switch-wrapper">
                <Switch
                  checked={values.gift_wrapping === "yes"}
                  onChange={(evt) =>
                    setFieldValue(
                      "gift_wrapping",
                      evt.currentTarget.checked ? "yes" : "no"
                    )
                  }
                  label=" "
                />
              </div>
            </div>
          </div>
        )}
        <div>
          <h2 className="font-bold text-xl my-6 text-left">Vehicle Details</h2>
          <div className={`${cardClassName} grid gap-4`}>
            <SelectComponent
              title="Please select the age of the receiving vehicle from the drop down menu:"
              name="year"
              label="Select vehicle year"
              options={vehicleYearOptions}
              onChange={(e) => {
                const target = e.target as typeof e.target & {
                  value: string;
                };
                setFieldValue("year", target.value);
              }}
              wrapperClassName="sm:block hidden"
              defaultValue={values.year}
              errorMsg={touched.year ? errors.year : ""}
            ></SelectComponent>
            <SelectComponent
              title="Please select the age of the receiving vehicle from the drop down menu:"
              name="year"
              label="Select year"
              options={vehicleYearOptions}
              onChange={(e) => {
                const target = e.target as typeof e.target & {
                  value: string;
                };
                setFieldValue("year", target.value);
              }}
              wrapperClassName="sm:hidden block"
              defaultValue={values.year}
              errorMsg={touched.year ? errors.year : ""}
            ></SelectComponent>
            <p className="text-left">
              <b>
                What is the name and/or company name that appears on the V5C UK
                Registration Certificate?
              </b>{" "}
              If you are in any doubt, please refer to the V5C.
            </p>
            <TextFieldComponent
              name="vehicle_keeper"
              wrapperClassName="w-full"
              className="w-full"
              label="Registered Keeper"
              placeholder="Registered Keeper"
              onChange={handleChange}
              value={values.vehicle_keeper}
              errorMsg={touched.vehicle_keeper ? errors.vehicle_keeper : ""}
            ></TextFieldComponent>
            <Collapsible text="Need help?" graphic="help" type="green">
              <div className="text-left text-xs grid gap-4 pt-4">
                <p>
                  <b>• I already have a vehicle for {registration}.</b>
                  <br />
                  <i>
                    The Nominee is the name shown as the Registered Keeper of
                    the vehicle.
                  </i>
                </p>
                <p>
                  <b>• I do not have a vehicle for {registration} yet.</b>
                  <br />
                  <i>
                    The Nominee is the name you intend to put as the Registered
                    Keeper of the vehicle.
                  </i>
                </p>
                <p>
                  <b>
                    • I am buying {registration} for a third party or as a gift.
                  </b>
                  <br />
                  <i>
                    This is the name of the recipient so please confirm their
                    Registered Keeper details before proceeding.
                  </i>
                </p>
                <img src={v5cnamearea} alt="" />
              </div>
            </Collapsible>
          </div>
        </div>
        {existDeliveryAndTransfer && (
          <>
            <div>
              <h2 className="font-bold text-xl my-6 text-left">
                Select Delivery Type
                <p className="text-left text-sm">
                  All options are actioned the same day paperwork is received by New Reg
                </p>
              </h2>
              <CheckoutCardRadios
                setFieldValue={setFieldValue}
                selectValue={values.delivery_type}
                radios={deliveryRadios}
                name="delivery_type"
              ></CheckoutCardRadios>

              <p className="text-center  text-xs">
                {(hadTryToSubmit.current && errors.delivery_type) || ""}
              </p>
            </div>
            <div>
              <h2 className="font-bold text-xl my-6 text-left">
                Select a Transfer Option
              </h2>
              <CheckoutCardRadios
                setFieldValue={setFieldValue}
                selectValue={values.transfer_type}
                radios={transferRadios}
                name="transfer_type"
              ></CheckoutCardRadios>
              <p className="text-center text-red-600 text-xs">
                {(hadTryToSubmit.current && errors.transfer_type) || ""}
              </p>
            </div>
          </>
        )}
        {salesFormMessage && <p className="text-sm mb-2">{salesFormMessage}</p>}
        {registrationDetails && (
          <div>
            <p className="text-sm">
              {registrationDetails.vtype == "1" ||
              registrationDetails.vtype == "0" ? (
                <>
                  Please note: This registration mark can be assigned to a{" "}
                  <b>car, van, heavy goods vehicle, moped or motorcycle</b> of
                  any age.
                </>
              ) : (
                <>
                  Please note: This registration mark can only be assigned to a
                  car, van, heavy goods vehicle, moped or motorcycle first
                  manufactured on or after{" "}
                  <b>
                    {registrationDetails.display_date1}. Unfortunately, it
                    cannot be assigned to an older vehicle.
                  </b>
                  &nbsp; If you do not have a vehicle, you may hold the number
                  on the DVLA certificate provided for up to 10 years, or longer for
                  an additional fee.
                </>
              )}
            </p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4 w-full mt-8">
          <ButtonRound
            buttonType="button"
            text="Go back"
            type="black"
            className="h-10 previous-page-btn"
            onClick={prevPage}
          ></ButtonRound>
          <ButtonRound
            buttonType="submit"
            text="Next page"
            type="success"
            className="h-10 submit-customise-btn"
            disabled={isSubmitting}
            onClick={() => {
              hadTryToSubmit.current = true;
            }}
          ></ButtonRound>
        </div>
      </form>
    </div>
  );
};

export { CheckoutCustomise };
